/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useEffect } from "react";
import { ChevronLeft, ChevronRight, AlertTriangle, Info, Loader2 } from "lucide-react";
import { Sheet } from "../ui/sheet";
import { Button } from "../ui/button";
import { useSelector } from "react-redux";
import { lastStep, step1st2nd, taskListComponent } from "./TaskList/TaskSteps";
import { useStoreDispatch } from "../../store/hooks";
import { setBasicDialogOpenFor, setConfirmDialog, setErrorMsg } from "../../store/helpers/helperReducers";
import { activeTaskStepsSelector, isEditTaskSelector, tasksSelector } from "../../store/api/tasks/selector";
import { setActiveTaskStep, setIsEditTask, setScheduledTask } from "../../store/api/tasks/reducer";
import { useTaskDisplayData } from "./hooks";
import { enableNext } from "./helpers";
import ConfirmDialog from "../Shared/Dialogs/ConfirmDialog";
import { confirmDialogSelector, errorMsgSelector } from "../../store/helpers/helperSelectors";
import { DIALOGOPENFOR } from "../../globals/globals";
import { currentSiteGroupsSelector, selectedTaskTypeSelector } from "../../store/api/admin/selectors";
import { getAllSiteGroupList } from "../helpers";

const AddScheduledTask: React.FC = () => {
    const dispatch = useStoreDispatch();
    const { submitHandler, createStatus } = useTaskDisplayData();

    const openConfirmDialog = useSelector(confirmDialogSelector);
    const scheduledtasks = useSelector(tasksSelector);
    const activeTaskStep = useSelector(activeTaskStepsSelector) || 0;
    const isEditTask = useSelector(isEditTaskSelector);
    const errorMsg = useSelector(errorMsgSelector);
    const selectionType = useSelector(selectedTaskTypeSelector);
    const currentSiteGroups = useSelector(currentSiteGroupsSelector);
    const groupList = getAllSiteGroupList(currentSiteGroups) || [];


    const selectedTaskName = scheduledtasks?.taskName?.value;

    const netxSteps = taskListComponent?.[selectedTaskName] || [];
    const steps = [...step1st2nd, ...netxSteps, lastStep];
    const maxSteps = steps.length;
    const rebootInputcheck = !!scheduledtasks?.radioNodeId || !!scheduledtasks?.radioUnitId || !!scheduledtasks?.sectorDeviceId
    const handleNext = () => {
        if (activeTaskStep === 2 && selectedTaskName === "reboot" && !rebootInputcheck) {
            dispatch(setConfirmDialog(DIALOGOPENFOR.REBOOTTASK));
        } else if (activeTaskStep === 2 && selectedTaskName === "softwareDownloadUpdate" && scheduledtasks?.sshOperation) {
            dispatch(setConfirmDialog(DIALOGOPENFOR.SOFTWAREDOWNLOADANDUPDATED));
        } else {
            dispatch(setActiveTaskStep(activeTaskStep + 1));

        }
    };

    const handleBack = () => {
        dispatch(setActiveTaskStep(activeTaskStep - 1));
    };
    const handleClose = () => {
        dispatch(setBasicDialogOpenFor(undefined));
        dispatch(setScheduledTask({}));
        dispatch(setErrorMsg({}));
        isEditTask && dispatch(setIsEditTask(!isEditTask));
    }
    useEffect(() => {
        if (isEditTask && selectedTaskName !== "fileManagementCreate") {
            dispatch(setScheduledTask({ ...scheduledtasks, password: "", newPassword: "" }));
        }
    }, [])

    const handleClickAction = (actionName: string) => {
        if (actionName?.match("yes")) {
            dispatch(setActiveTaskStep(activeTaskStep + 1));
        }
    };

    const dialogContent = {
        reboot: (openConfirmDialog === DIALOGOPENFOR.REBOOTTASK && (
            <ConfirmDialog actionName={`Yes`} handleClickAction={handleClickAction}>
                <div className="flex">
                    <div className="pr-3">
                        <AlertTriangle className="h-7 w-7 text-yellow-500" />
                    </div>
                    <div>
                        {!!scheduledtasks?.cleanOption ? <p className="text-sm font-medium"> This Task will clean {`'${scheduledtasks?.cleanOption}'`} and reboot the devices.</p>
                            : <p className="text-sm font-medium"> If Radio Node or Radio Unit is not selected, device(s) will be rebooted.</p>}
                        <p className="text-sm font-medium"> Do you want to continue?</p>
                    </div>
                </div>

            </ConfirmDialog>
        )),
        softwareDownloadUpdate: (openConfirmDialog === DIALOGOPENFOR.SOFTWAREDOWNLOADANDUPDATED && scheduledtasks?.sshOperation && (
            <ConfirmDialog actionName={`Yes`} handleClickAction={handleClickAction}>
                <div className="flex">
                    <div className="pr-3">
                        <Info className="h-7 w-7 text-primary" />
                    </div>
                    <div>
                        <p className="text-sm font-medium"> Please make sure that SSH key index is generated on Node and copied to Host server path-&apos;/user/.ssh/authorized_keys&apos;</p>
                    </div>
                </div>

            </ConfirmDialog>
        )),
    } as { [key: string]: React.ReactNode }

    const isBackDisabled = activeTaskStep === 0 || ["Database Backup", "Database Integrity", "NodeDmBackupTask", "NodeDbBackupTask"]?.includes(scheduledtasks?.name);

    const footer = (
        <div className="flex w-full items-center justify-between">
            <Button
                variant="outline"
                size="sm"
                onClick={handleBack}
                disabled={isBackDisabled}
                className="gap-1"
            >
                <ChevronLeft className="h-4 w-4" />
                Back
            </Button>

            {/* Step dots */}
            <div className="flex items-center gap-1.5">
                {Array.from({ length: maxSteps }).map((_, i) => (
                    <span
                        key={i}
                        className={`inline-block h-2 w-2 rounded-full transition-colors ${
                            i === activeTaskStep ? "bg-primary" : "bg-border"
                        }`}
                    />
                ))}
            </div>

            <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleClose}>Cancel</Button>
                {activeTaskStep === maxSteps - 1 ? (
                    <Button
                        size="sm"
                        disabled={!!!scheduledtasks?.name || !!errorMsg?.name || createStatus.isLoading || !!errorMsg?.startAt}
                        onClick={submitHandler}
                        className="gap-1"
                    >
                        {createStatus.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                        Save
                    </Button>
                ) : (
                    <Button
                        size="sm"
                        disabled={!enableNext(scheduledtasks, activeTaskStep, errorMsg, selectionType, groupList)}
                        onClick={handleNext}
                        className="gap-1"
                    >
                        Next
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                )}
            </div>
        </div>
    );

    return (
        <>
            {!!scheduledtasks.taskName.value && dialogContent[scheduledtasks.taskName.value]}
            <Sheet open={true} onClose={handleClose} title={isEditTask ? "Edit a task" : "Create a task"} footer={footer} width="560px">
                {!!steps[activeTaskStep]?.title && (
                    <div className="w-full pb-2">
                        <p className="text-sm font-bold">{steps[activeTaskStep]?.title}</p>
                    </div>
                )}
                <div className="w-full">
                    {steps[activeTaskStep].component}
                </div>
            </Sheet>
        </>

    );
};
export default AddScheduledTask;